'use client';

import type { ReactNode } from 'react';

import { Dropdown } from '#/components/Dropdown/Dropdown';
import { type LocationRadiusSelectHandle } from '#/components/LocationRadiusSelect/LocationRadiusSelect';
import { MobileSearchMenu } from '#/components/MobileSearchMenu/MobileSearchMenu';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useSearchRadius } from '#/modules/search/contexts/SearchContext';
import { useSearchRadiusOptions } from '#/modules/search/hooks/facets/useSearchRadiusOptions';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import type { SearchFromPageType } from '#/modules/search/types/SearchFromPageType';
import type { SearchType } from '#/modules/search/types/SearchType';

import { LayoutHeaderMobileSearchHolder } from './LayoutHeader.styled';
import { useIsMobileSearchV2Enabled } from './MobileSearchV2/useIsMobileSearchV2Enabled';
import { useLayoutHeaderSearch } from './useLayoutHeaderSearch';

type Props = {
  /**
   * Trigger is passed so that LayoutHeaderButton doesn't become 'use client'
   */
  trigger: ReactNode;
  defaultSearchType: SearchType;
  searchFromPageType: SearchFromPageType | undefined;
  isSearchLandingPage: boolean;
  isSearchPage: boolean;
};

export function LayoutHeaderMobileSearchMenu({
  trigger,
  defaultSearchType,
  searchFromPageType,
  isSearchLandingPage,
  isSearchPage,
}: Props) {
  const { i18n } = useI18n();

  const {
    initialQuery,
    searchLocation,
    changeSearchLocation,
    search,
    searchOnClear,
  } = useLayoutHeaderSearch({
    defaultSearchType,
    isSearchPage,
    isSearchLandingPage,
    embedSearchWidgetId: null,
    searchFromPageType,
  });

  const radius = useSearchRadius();
  const { setSearchUrlRadius } = useSearchPageActions({
    isSearchLandingPage,
  });

  const radiusOptions = useSearchRadiusOptions();

  const radiusHandle: LocationRadiusSelectHandle = isSearchPage
    ? { type: 'controlled', onChange: setSearchUrlRadius, radius }
    : { type: 'uncontrolled', radius };

  const isNewMobileSearch = useIsMobileSearchV2Enabled();

  if (isNewMobileSearch) return null;

  return (
    <LayoutHeaderMobileSearchHolder>
      <Dropdown
        summaryAriaLabel={i18n('Search')}
        trigger={trigger}
        trackClickedOpen={{
          name: 'Header Utility CTA - Search',
        }}
      >
        <MobileSearchMenu
          defaultSearchType={defaultSearchType}
          initialQuery={initialQuery}
          searchLocation={searchLocation}
          onChangeSearchLocation={changeSearchLocation}
          radiusOptions={radiusOptions}
          radiusHandle={radiusHandle}
          onSearch={search}
          searchOnClear={searchOnClear}
        />
      </Dropdown>
    </LayoutHeaderMobileSearchHolder>
  );
}
